import type { CSSProperties } from 'react';
import { forwardRef, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import type {
  AutoSizeValue,
  LinkValue,
  PositionWithOffsetValue
} from '@elfsight/configurator-contracts';
import type { SubmitActionType } from '@elfsight/widget-form-builder/configurator';
import type { WidgetBackgroundProps } from '@elfsight/widget-ui';
import { openWindowByLink } from '@elfsight/widget-utils';
import { useRect } from 'react-use-rect';
import type { BlockProps } from '../types';
import type { BuilderBlockProps } from './Builder';
import { Builder } from './Builder';
import {
  getAlignmentBlock,
  getVisibleBlocks,
  useApplyMobileSettings,
  useNarrowView
} from './utils';

export type BannerProps = BuilderBlockProps & {
  bannerWidth?: number;
  editMode?: boolean;
  verticalBannerHeight: AutoSizeValue;
  horizontalBannerHeight?: AutoSizeValue;
  bannerLink?: LinkValue;
  contentAlignment: PositionWithOffsetValue;
  contentPadding?: number;
  widgetToken: string | undefined;
  submitUrl?: string;
  submitAdditionalData?: unknown;
  triggerBackgroundImageAnimationEffect?: WidgetBackgroundProps['triggerImageAnimation'];
  onBannerLinkClick?: (value: string) => void;
};

export const Banner = forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      editMode = false,
      widgetId,
      bannerBackground,
      triggerBackgroundImageAnimationEffect,
      stretchBackground,
      bannerWidth,
      bannerLink,
      verticalBannerHeight,
      horizontalBannerHeight,
      contentAlignment,
      contentPadding,
      contentHorizontalPadding,
      contentVerticalPadding,
      contentDirection = 'column',
      widgetToken,
      submitUrl,
      submitAdditionalData,
      blocks,
      style,
      adaptiveMedia,
      onBannerLinkClick,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const isColumn = contentDirection === 'column';
    const { position: contentAlignmentValue } = contentAlignment;

    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);

    const [bannerRef] = useRect(({ height }) => setContentHeight(height), {
      resize: true
    });

    const { isNarrowViewport, isNarrow } = useNarrowView(bannerWidth);
    const applyMobileSettings = useApplyMobileSettings(bannerWidth);

    const formBlockProps = useMemo(
      () => ({
        widgetToken,
        submitUrl,
        submitAdditionalData,
        onSubmitMessageButtonClick: () => setIsFormSubmitted(false),
        onSubmitSuccessful: (submitAction: SubmitActionType) =>
          submitAction === 'message' && setIsFormSubmitted(true)
      }),
      [widgetToken, submitUrl, submitAdditionalData]
    );

    const notFormBlockProps = useMemo(
      () => ({
        style: isFormSubmitted ? { display: 'none' } : undefined
      }),
      [isFormSubmitted]
    );

    const getBlockParams = useMemo(
      () => (block: BlockProps) => ({
        ...block,
        editMode,
        widgetId,
        applyMobileSettings,
        isFormSubmitted,
        alignment: getAlignmentBlock('block', contentAlignmentValue),
        visible:
          isFormSubmitted && block.type !== 'form' ? false : block.visible,
        ...(block.type === 'form' ? formBlockProps : notFormBlockProps)
      }),
      [
        editMode,
        applyMobileSettings,
        isFormSubmitted,
        contentAlignmentValue,
        formBlockProps,
        notFormBlockProps
      ]
    );

    const bannerBlocks = useMemo(() => {
      if (!blocks) return [];

      return getVisibleBlocks({
        blocks,
        isColumn,
        isNarrowViewport
      }).map(getBlockParams);
    }, [blocks, isColumn, isNarrowViewport, getBlockParams]);

    const horizontalStyle: CSSProperties = {
      height: 'auto',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center'
    };

    const bannerHeight = useMemo(() => {
      if (isFormSubmitted) {
        return contentHeight;
      }

      return isColumn ? verticalBannerHeight : horizontalBannerHeight;
      // contentHeight isn't used in dependencies because it's not needed to calculate it
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isFormSubmitted,
      isColumn,
      verticalBannerHeight,
      horizontalBannerHeight
    ]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (bannerLink?.rawValue) {
        openWindowByLink(e, bannerLink);
        onBannerLinkClick?.(bannerLink.rawValue);
      }
    };

    return (
      <Container
        _disabledScroll={adaptiveMedia}
        _stretchBackground={stretchBackground}
        ref={bannerRef}
        onClick={handleClick}
        style={{
          ...style,
          cursor: bannerLink?.rawValue && 'pointer'
        }}
      >
        <Builder
          {...forwardingProps}
          stretchBackground={stretchBackground}
          widgetId={widgetId}
          ref={forwardingRef}
          blocks={bannerBlocks}
          contentDirection={contentDirection}
          contentStyle={isColumn ? {} : horizontalStyle}
          adaptiveMedia={adaptiveMedia}
          mobileView={isNarrow}
          bannerBackground={{
            ...bannerBackground,
            triggerImageAnimation: triggerBackgroundImageAnimationEffect
          }}
          bannerWidth={bannerWidth}
          bannerHeight={bannerHeight}
          contentHorizontalPadding={contentHorizontalPadding || contentPadding}
          contentVerticalPadding={contentVerticalPadding || contentPadding}
          backgroundContentStyle={isColumn ? {} : { marginBlock: 'auto' }}
          contentAlignment={
            isFormSubmitted
              ? 'center'
              : getAlignmentBlock('content', contentAlignmentValue)
          }
        />
      </Container>
    );
  }
);

const Container = styled.div<{
  _disabledScroll?: boolean;
  _stretchBackground?: boolean;
}>`
  ${({ _stretchBackground, _disabledScroll }) => css`
    display: flex;
    position: relative;
    height: 100%;
    overflow: ${_disabledScroll ? 'hidden' : 'auto'};
    ${_stretchBackground
      ? css`
          max-width: 100%;
        `
      : css`
          max-width: fit-content;
          margin-inline: auto;
        `}

    & div:empty {
      display: block;
    }
  `}
`;
